import React from 'react';
import { getMapLegendColors, scoreColors } from '../../utils/coloring';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import './GradientLegend.css';

const GradientLegend = ({ min, max, activeTab, range, nationalRate, compareMode, rateViewMode }) => {
  const getComparisonRate = () => {
    if (!rateViewMode || typeof compareMode !== 'object') return nationalRate;
    
    return nationalRate;
  };

  const formatValue = (value) => {
    if (!value && value !== 0) return '';

    if (value === Infinity || value === -Infinity) {
      let formattedInf;
      if (range && (range.includes('diff_pct') || range === 'trend_diff_pct')) {
        formattedInf = value === Infinity ? '+Inf%' : '-Inf%';
      } else {
        formattedInf = 'Inf';
      }
      return <span style={{ color: 'var(--red)' }}>{formattedInf}</span>;
    }

    if (range) {
      if (range === 'percentile' || range === 'composite_score_percentile') {
        return value === 0 ? '1st' : formatPercentile(value);
      }
      if (range === 'rank' || range === 'composite_score_rank') {
        return formatRank(value); 
      }
      
      const comparisonRate = getComparisonRate();
      
      if (range === 'rate') {
        const formattedValue = formatNumber(value);
        return value === comparisonRate ? 
          formattedValue : 
          value < comparisonRate ? 
            <span style={{ color: 'var(--green)' }}>{formattedValue}</span> : 
            <span style={{ color: 'var(--red)' }}>{formattedValue}</span>;
      }
      
      if (range === 'rate_diff' || range === 'rate_vs_national_rate') {
        const formattedValue = formatDifference(value, false, true);
        return value === 1 ? 
          formattedValue : 
          value < 1 ? 
            <span style={{ color: 'var(--green)' }}>{formattedValue}</span> : 
            <span style={{ color: 'var(--red)' }}>{formattedValue}</span>;
      }
      if (range.includes('diff_pct') || range === 'trend_diff_pct') {
        const formattedValue = formatDifference(value, true);
        return value === 0 ?  
          formattedValue : 
          value < 0 ? 
            <span style={{ color: 'var(--green)' }}>{formattedValue}</span> : 
            <span style={{ color: 'var(--red)' }}>{formattedValue}</span>;
      }
      if (range.includes('diff') || range === 'count_vs_avg' || range === 'count_vs_prev') {
        const formattedValue = formatDifference(value);
        return value === 0 ? 
          formattedValue : 
          value < 0 ? 
            <span style={{ color: 'var(--green)' }}>{formattedValue}</span> : 
            <span style={{ color: 'var(--red)' }}>{formattedValue}</span>;
      }
      if (range === 'score' || range === 'composite_score') {
        return formatNumber(value, true);
      }
      return formatNumber(value);
    }

    switch (activeTab) {
      case 'trends':
        return formatDifference(value, true);
      case 'rates':
        return formatNumber(value);
      case 'scores':
        return formatNumber(value, true);
      default:
        return formatNumber(value);
    }
  };

  const getEffectiveTab = () => {
    if (!range) return activeTab;

    if (range === 'score' || range === 'composite_score') {
      return 'scores';
    }
    if (range.includes('diff') || range === 'count_vs_avg' || range === 'trend_diff_pct' || range === 'count_vs_prev') {
      return 'trends';
    }
    if (range === 'rate' || range === 'national_rate' || range === 'rate_vs_national_rate') {
      return 'rates';
    }
    return 'reports';
  };

  if (getEffectiveTab() === 'scores') {
    return (
      <div className="gradient-legend-container">
        <div className="gradient-legend-wrapper">
          <div className="gradient-value">0</div>
          <div className="gradient-bar" style={{ display: 'flex', padding: 0 }}>
            {scoreColors.map(({ color }, index) => (
              <div
                key={color}
                style={{ 
                  flex: 1,
                  backgroundColor: color,
                  opacity: 0.8,
                  borderRight: index < scoreColors.length - 1 ? '1px solid rgba(26, 44, 78, 0.6)' : 'none'
                }}
              />
            ))}
          </div>
          <div className="gradient-value">100</div>
        </div>
      </div>
    );
  }

  if (range === 'count_avg' || range === 'national_rate') {
    return (
      <div className="gradient-legend-container">
        <div className="gradient-legend-wrapper" style={{ justifyContent: 'center', width: '60%' }}>
          <div 
            className="gradient-bar"
            style={{ 
              background: 'var(--silver)',
              opacity: 0.8,
              border: '2px solid var(--navy)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            {formatValue(max)}
          </div>
        </div>
      </div>
    );
  }

  let displayMin = min;
  let displayMax = max;

  if (range === 'rank' || range === 'composite_score_rank') {
    displayMin = 1;
  } else if (range === 'percentile' || range === 'composite_score_percentile') {
    displayMin = 0;
    displayMax = 100;
  }

  const colors = getMapLegendColors(getEffectiveTab(), displayMin, displayMax, nationalRate, range);
  
  if (!colors || colors.length === 0 || 
    (displayMin === undefined || displayMax === undefined) || 
    (displayMin === null && displayMax === null) || 
    !isFinite(displayMin) || 
    (displayMin === displayMax && 
     range !== 'percentile' && range !== 'composite_score_percentile')) {
   return null;
 }
  
  const useColors = (range === 'rank' || range === 'composite_score_rank') ? [...colors].reverse() : colors;  
  const gradientString = `linear-gradient(to right, ${Array.isArray(useColors) ? useColors.join(', ') : useColors})`;
  
  return (
    <div className="gradient-legend-container">
      <div className="gradient-legend-wrapper">
        <div className="gradient-value">
          {formatValue(displayMin)}
        </div>
        <div 
          className="gradient-bar"
          style={{ background: gradientString }}
        />
        <div className="gradient-value">
          {formatValue(displayMax)}
        </div>
      </div>
    </div>
  );
};

export default React.memo(GradientLegend);